/* eslint-disable react/display-name */
import type { ReactNode } from "react";
import { Skeleton } from "primereact/skeleton";
import { ProgressBar } from "primereact/progressbar";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

// ---------------------------------------------------------------------------
// Skeleton utilities
// ---------------------------------------------------------------------------

/**
 * Returns an array of `count` null values typed as `(T | null)[]`.
 * Pass as the DataTable `value` prop while loading to render skeleton rows:
 *
 *   value={isLoading ? skeletonRows<MyRow>(DEFAULT_ROW_COUNT) : data}
 *
 * Column body functions should accept `T | null` and return a Skeleton for null.
 */
export const skeletonRows = <T,>(count: number): (T | null)[] =>
  Array.from({ length: count }, () => null);

/**
 * Wraps a body render function with a null guard that shows a Skeleton instead.
 * The returned function accepts `T | null` — pass it directly as a Column `body` prop.
 *
 *   body={withSkeleton<MyRow>(row => row.name ?? "--", "8rem")}
 */
export const withSkeleton =
  <T,>(render: (rowData: T) => ReactNode, skeletonWidth?: string) =>
  (rowData: T | null): ReactNode => {
    if (rowData == null) return <Skeleton width={skeletonWidth} />;
    return render(rowData);
  };

// ---------------------------------------------------------------------------
// Generic body factories
// ---------------------------------------------------------------------------

/**
 * Body that renders a plain string field with an optional fallback.
 *
 *   body={textBody<MyRow>("firstName")}
 */
export const textBody =
  <T,>(field: keyof T, fallback = "--") =>
  (rowData: T | null): ReactNode => {
    if (rowData == null) return <Skeleton />;
    const value = rowData[field];
    return (value as string | null | undefined) ?? fallback;
  };

/**
 * Body that formats a date string field using `humanDateWithoutTime`.
 * Null, empty, "null", or "NULL" values render `nullLabel` (default "--").
 *
 *   body={dateBody<MyRow>("completedAt", "Not completed")}
 */
export const dateBody =
  <T,>(field: keyof T, nullLabel = "--") =>
  (rowData: T | null): ReactNode => {
    if (rowData == null) return <Skeleton width="7rem" />;
    const value = rowData[field] as string | null | undefined;
    if (!value || value === "null" || value === "NULL") return nullLabel;
    try {
      return humanDateWithoutTime(value);
    } catch {
      return nullLabel;
    }
  };

/**
 * Body that renders a numeric score field (0–100) as a ProgressBar with a percentage label.
 * Null scores render "--".
 *
 *   body={scoreProgressBody<MyRow>("averageScore", themeClasses.bgSurface600)}
 */
export const scoreProgressBody =
  <T,>(scoreField: keyof T, progressBarClassName?: string) =>
  (rowData: T | null): ReactNode => {
    if (rowData == null) return <Skeleton width="6rem" />;
    const score = rowData[scoreField] as number | null | undefined;
    if (score == null) return "--";
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ProgressBar
            value={score}
            showValue={false}
            className={progressBarClassName}
            style={{ height: "0.5rem" }}
          />
        </div>
        <span className="body-sm">{score}%</span>
      </div>
    );
  };

// ---------------------------------------------------------------------------
// Status body
// ---------------------------------------------------------------------------

export interface StatusEntry {
  /** Pi icon class suffix, e.g. "pi-check-circle". Omit for no icon. */
  icon?: string;
  /** Fully-resolved CSS class string, e.g. "text-warning". */
  colorClass?: string;
  label: string;
}

/**
 * Generic status body renderer.
 *
 * Pass a `resolve` function that maps a row to a `StatusEntry`. The caller is
 * responsible for converting domain-specific config values (e.g. `"warning"`)
 * to their final class strings (e.g. `themeClasses.textWarning`).
 *
 *   body={statusBody<MyRow>(row => ({
 *     icon: config.icon || undefined,
 *     colorClass: config.color === "warning" ? themeClasses.textWarning : config.color,
 *     label: row.statusName ?? "--",
 *   }))}
 */
export const statusBody =
  <T,>(resolve: (row: T) => StatusEntry, skeletonWidth = "6rem") =>
  (rowData: T | null): ReactNode => {
    if (rowData == null) return <Skeleton width={skeletonWidth} />;
    const { icon, colorClass = "", label } = resolve(rowData);
    return (
      <div className="flex items-center gap-2">
        {icon ? <i className={`pi ${icon} ${colorClass}`} /> : null}
        <span className={colorClass}>{label}</span>
      </div>
    );
  };

// ---------------------------------------------------------------------------
// Proficiency dot body
// ---------------------------------------------------------------------------

const PROFICIENCY_DOT_COLORS: Record<string, string> = {
  foundational: themeClasses.bgIndigoDataviz1,
  intermediate: themeClasses.bgIndigoDataviz2,
  advanced: themeClasses.bgIndigoDataviz3,
};

/**
 * Body that renders a proficiency level field as a colored dot + capitalized label.
 *
 *   body={proficiencyDotBody<MyRow>("proficiency")}
 */
export const proficiencyDotBody =
  <T,>(field: keyof T) =>
  (rowData: T | null): ReactNode => {
    if (rowData == null) return <Skeleton />;
    const proficiency = rowData[field] as string | null | undefined;
    if (!proficiency) return "--";
    return (
      <div className="flex items-center gap-2">
        <span
          className={`inline-block w-2 h-2 rounded-full ${PROFICIENCY_DOT_COLORS[proficiency] ?? themeClasses.bgSurface600}`}
        />
        <span className="capitalize">{proficiency}</span>
      </div>
    );
  };
